import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../lib/api';
import {
  LayoutDashboard,
  Package,
  Users,
  Truck,
  ShoppingCart,
  FileText,
  Receipt,
  CreditCard,
  PieChart,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Building2,
  ClipboardList,
  Boxes,
  TrendingUp,
  BookOpen,
  Calculator,
  BarChart3,
  Shield,
  PackageCheck,
  Wallet,
  Zap,
  UserPlus,
  PackageOpen,
  Factory,
  Hammer,
  ScrollText,
  Clock,
  FileSearch,
  Tags,
  Briefcase,
  GalleryVerticalEnd,
  Layers3,
  BadgeCheck,
  Store,
  ExternalLink,
  Loader2,
  ContactRound,
  DatabaseZap,
  Landmark,
  ScanLine,
  ArrowRightLeft,
  Wrench,
  ShieldAlert,
  Scale,
  MapPinned,
  HardHat,
  ShieldCheck,
  Ruler,
  TicketPercent,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import NotificationBell from './NotificationBell';

const MENU_SECTIONS = [
  { id: 'growth', label: 'AI Sales & Growth', icon: TrendingUp, items: [
    { icon: BarChart3, label: 'Growth Dashboard', path: '/growth/dashboard', module: 'growth' },
    { icon: ContactRound, label: 'Sales Leads', path: '/growth/leads', module: 'growth' },
    { icon: Settings, label: 'Growth Settings', path: '/growth/settings', module: 'growth' },
  ] },
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    items: [
      { icon: LayoutDashboard, label: 'Business Dashboard', path: '/dashboard', module: 'dashboard' },
    ],
  },
  {
    id: 'storefront',
    label: 'Storefront & Catalogue',
    icon: Store,
    items: [
      { icon: Package, label: 'Products', path: '/products', module: 'products' },
      { icon: Layers3, label: 'Categories', path: '/categories', module: 'categories' },
      { icon: BadgeCheck, label: 'Brands', path: '/brands', module: 'brands' },
      { icon: GalleryVerticalEnd, label: 'Collections', path: '/collections', module: 'collections' },
      { icon: Ruler, label: 'Size Guides', path: '/size-guides', module: 'size-guides' },
      { icon: TicketPercent, label: 'Coupons & Promotions', path: '/coupons', module: 'coupons' },
      { icon: PackageOpen, label: 'Packaging Rules', path: '/packaging-rules', module: 'packaging-rules' },
      { icon: Settings, label: 'Storefront Settings', path: '/settings?tab=storefront', module: 'settings' },
    ],
  },
  {
    id: 'sales',
    label: 'Orders & Customers',
    icon: ShoppingCart,
    items: [
      { icon: ShoppingCart, label: 'Sales Orders', path: '/sales-orders', module: 'sales-orders' },
      { icon: Truck, label: 'COD Settlements', path: '/cod-settlements', module: 'cod-settlements' },
      { icon: ContactRound, label: 'Customers', path: '/customers', module: 'customers' },
      { icon: FileText, label: 'Invoices', path: '/invoices', module: 'invoices' },
      { icon: CreditCard, label: 'Payments', path: '/payments', module: 'payments' },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing & Inventory',
    icon: Boxes,
    items: [
      { icon: Boxes, label: 'Inventory', path: '/inventory', module: 'inventory' },
      { icon: Truck, label: 'Suppliers', path: '/suppliers', module: 'suppliers' },
      { icon: ClipboardList, label: 'Purchase Orders', path: '/purchase-orders', module: 'purchase-orders' },
      { icon: PackageCheck, label: 'Goods Received (GRN)', path: '/grn', module: 'grn' },
    ],
  },
  {
    id: 'assets',
    label: 'Fixed Assets',
    icon: Landmark,
    items: [
      { icon: LayoutDashboard, label: 'Asset Dashboard', path: '/fixed-assets', module: 'fixed-assets' },
      { icon: Boxes, label: 'Asset Register', path: '/fixed-assets/register', module: 'fixed-assets' },
      { icon: ShoppingCart, label: 'Asset Acquisitions', path: '/fixed-assets/acquisitions', module: 'asset-acquisitions' },
      { icon: PackageCheck, label: 'Capitalizations', path: '/fixed-assets/capitalizations', module: 'asset-depreciation' },
      { icon: Calculator, label: 'Depreciation Runs', path: '/fixed-assets/depreciation', module: 'asset-depreciation' },
      { icon: ArrowRightLeft, label: 'Transfers', path: '/fixed-assets/transfers', module: 'asset-operations' },
      { icon: Users, label: 'Assignments', path: '/fixed-assets/assignments', module: 'asset-operations' },
      { icon: Wrench, label: 'Maintenance', path: '/fixed-assets/maintenance', module: 'asset-operations' },
      { icon: ShieldAlert, label: 'Impairments', path: '/fixed-assets/impairments', module: 'asset-depreciation' },
      { icon: Scale, label: 'Revaluations', path: '/fixed-assets/revaluations', module: 'asset-depreciation' },
      { icon: Receipt, label: 'Disposals', path: '/fixed-assets/disposals', module: 'asset-depreciation' },
      { icon: ScanLine, label: 'Stocktakes', path: '/fixed-assets/stocktakes', module: 'asset-operations' },
      { icon: BarChart3, label: 'Asset Reports', path: '/fixed-assets/reports', module: 'asset-reports' },
      { icon: Tags, label: 'Asset Categories', path: '/fixed-assets/categories', module: 'asset-settings' },
      { icon: MapPinned, label: 'Asset Master Data', path: '/fixed-assets/master-data', module: 'asset-settings' },
      { icon: HardHat, label: 'Capital Work in Progress', path: '/fixed-assets/cwip', module: 'asset-acquisitions' },
      { icon: Settings, label: 'Asset Settings', path: '/fixed-assets/settings', module: 'asset-settings' },
    ],
  },
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    icon: Factory,
    items: [
      { icon: LayoutDashboard, label: 'Manufacturing Dashboard', path: '/manufacturing', module: 'manufacturing' },
      { icon: Hammer, label: 'Raw Materials', path: '/raw-materials', module: 'raw-materials' },
      { icon: Truck, label: 'RM Suppliers', path: '/rm-suppliers', module: 'rm-suppliers' },
      { icon: ClipboardList, label: 'RM Purchase Orders', path: '/rm-purchase-orders', module: 'rm-purchase-orders' },
      { icon: PackageCheck, label: 'RM GRN', path: '/rm-grn', module: 'rm-grn' },
      { icon: Receipt, label: 'RM GRN Returns', path: '/rm-grn-returns', module: 'rm-grn-returns' },
      { icon: ScrollText, label: 'Bill of Materials', path: '/bom', module: 'bill-of-materials' },
      { icon: Factory, label: 'Work Orders', path: '/work-orders', module: 'work-orders' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Accounting',
    icon: TrendingUp,
    items: [
      { icon: Zap, label: 'Guided Quick Transactions', path: '/quick-transactions', module: 'quick-transactions', highlight: true },
      { icon: ShieldCheck, label: 'Finance Control Centre', path: '/finance-control-centre', module: 'finance-controls' },
      { icon: TrendingUp, label: 'Accounting Entries', path: '/accounting', module: 'accounting' },
      { icon: UserPlus, label: 'Investors', path: '/investors', module: 'investors' },
      { icon: Wallet, label: 'Bank Accounts', path: '/bank-accounts', module: 'bank-accounts' },
      { icon: CreditCard, label: 'PayHere Settlements', path: '/payhere-settlements', module: 'payhere-settlements' },
      { icon: BookOpen, label: 'Chart of Accounts', path: '/chart-of-accounts', module: 'chart-of-accounts' },
      { icon: Calculator, label: 'General Ledger', path: '/general-ledger', module: 'general-ledger' },
      { icon: PieChart, label: 'Business Reports', path: '/reports', module: 'reports' },
      { icon: BarChart3, label: 'Financial Reports', path: '/financial-reports', module: 'financial-reports' },
    ],
  },
  {
    id: 'people',
    label: 'People & Payroll',
    icon: Users,
    items: [
      { icon: Building2, label: 'Departments', path: '/departments', module: 'departments' },
      { icon: Briefcase, label: 'Designations', path: '/designations', module: 'designations' },
      { icon: Users, label: 'Employees', path: '/employees', module: 'employees' },
      { icon: Clock, label: 'Attendance', path: '/attendance', module: 'attendance' },
      { icon: Calculator, label: 'Salary Structure', path: '/salary-structure', module: 'salary-structure' },
      { icon: Receipt, label: 'Leave Management', path: '/leave-management', module: 'leave-management' },
      { icon: Wallet, label: 'Advances & Loans', path: '/advances', module: 'advances' },
      { icon: ClipboardList, label: 'Task Assignments', path: '/task-assignments', module: 'task-assignments' },
      { icon: CreditCard, label: 'Payroll', path: '/payroll', module: 'payroll' },
      { icon: BarChart3, label: 'Payroll Reports', path: '/payroll-reports', module: 'payroll-reports' },
      { icon: Tags, label: 'Task Categories', path: '/task-categories', module: 'task-categories' },
    ],
  },
  {
    id: 'workspace',
    label: 'My Workspace',
    icon: ClipboardList,
    items: [
      { icon: LayoutDashboard, label: 'My Dashboard', path: '/my-dashboard', module: 'my-dashboard' },
      { icon: ClipboardList, label: 'My Tasks', path: '/my-tasks', module: 'my-tasks' },
      { icon: Users, label: 'My Profile', path: '/my-profile', module: 'my-dashboard' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: Shield,
    items: [
      { icon: Settings, label: 'Company Settings', path: '/settings?tab=company', module: 'settings' },
      { icon: Bell, label: 'Notifications', path: '/notifications', module: 'notifications' },
      { icon: Users, label: 'User Management', path: '/user-management', module: 'user-management' },
      { icon: Shield, label: 'System Admin', path: '/system-admin', module: 'system-admin' },
      { icon: DatabaseZap, label: 'ERP Validation Centre', path: '/erp-validation', module: 'system-admin' },
      { icon: FileSearch, label: 'Audit Logs', path: '/audit-logs', module: 'audit-logs' },
      { icon: BookOpen, label: 'Documentation', path: '/documentation', module: 'documentation' },
    ],
  },
];

const ROLE_MODULES = {
  admin: ['*'],
  manager: [
    'growth',
    'dashboard', 'products', 'categories', 'brands', 'collections', 'size-guides', 'coupons', 'inventory', 'grn', 'packaging-rules',
    'customers', 'suppliers', 'sales-orders', 'cod-settlements', 'invoices', 'purchase-orders', 'payments',
    'manufacturing', 'raw-materials', 'bill-of-materials', 'work-orders',
    'rm-suppliers', 'rm-purchase-orders', 'rm-grn', 'rm-grn-returns',
    'departments', 'designations', 'employees', 'attendance', 'salary-structure',
    'leave-management', 'advances', 'task-assignments', 'payroll', 'payroll-reports',
    'task-categories', 'reports', 'my-dashboard', 'my-tasks', 'settings',
    'fixed-assets', 'asset-acquisitions', 'asset-depreciation', 'asset-operations', 'asset-reports',
    'user-management', 'documentation', 'notifications',
  ],
  accountant: [
    'dashboard', 'accounting', 'chart-of-accounts', 'general-ledger', 'financial-reports',
    'invoices', 'payments', 'cod-settlements', 'investors', 'quick-transactions', 'finance-controls', 'bank-accounts',
    'payhere-settlements', 'reports', 'fixed-assets', 'asset-acquisitions', 'asset-depreciation', 'asset-reports', 'asset-settings', 'my-dashboard', 'my-tasks', 'documentation', 'notifications',
  ],
  accounts: [
    'dashboard', 'accounting', 'chart-of-accounts', 'general-ledger', 'financial-reports',
    'invoices', 'payments', 'cod-settlements', 'investors', 'quick-transactions', 'finance-controls', 'bank-accounts',
    'payhere-settlements', 'reports', 'fixed-assets', 'asset-acquisitions', 'asset-depreciation', 'asset-reports', 'asset-settings', 'my-dashboard', 'my-tasks', 'documentation', 'notifications',
  ],
  store: [
    'dashboard', 'products', 'categories', 'brands', 'collections', 'size-guides', 'inventory', 'grn', 'packaging-rules',
    'suppliers', 'purchase-orders', 'raw-materials', 'rm-suppliers', 'rm-purchase-orders',
    'rm-grn', 'rm-grn-returns', 'fixed-assets', 'asset-acquisitions', 'asset-operations', 'my-dashboard', 'my-tasks', 'documentation', 'notifications',
  ],
  employee: ['my-dashboard', 'my-tasks', 'attendance', 'leave-management', 'fixed-assets', 'asset-operations', 'documentation', 'notifications'],
  staff: ['my-dashboard', 'my-tasks', 'attendance', 'leave-management', 'fixed-assets', 'asset-operations', 'documentation', 'notifications'],
};

const hasModuleAccess = (role, module, employeePermissions = null) => {
  if (role === 'admin') return true;
  if (employeePermissions && Array.isArray(employeePermissions) && employeePermissions.length > 0) {
    return employeePermissions.includes(module);
  }
  const modules = ROLE_MODULES[role] || ROLE_MODULES.employee;
  return modules.includes('*') || modules.includes(module);
};

const targetParts = (target) => {
  const [pathname, search = ''] = target.split('?');
  return { pathname, search: search ? `?${search}` : '' };
};

const isItemActive = (item, location) => {
  const target = targetParts(item.path);
  if (target.search) {
    return location.pathname === target.pathname && location.search === target.search;
  }
  return location.pathname === target.pathname && !(
    target.pathname === '/settings' && location.search === '?tab=storefront'
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openingPreview, setOpeningPreview] = useState(false);
  const employeePermissions = user?.employee_permissions;

  const visibleSections = useMemo(
    () => MENU_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => hasModuleAccess(user?.role, item.module, employeePermissions)),
      }))
      .filter((section) => section.items.length > 0),
    [user?.role, employeePermissions],
  );

  const activeSectionId = useMemo(
    () => visibleSections.find((section) => section.items.some((item) => isItemActive(item, location)))?.id,
    [visibleSections, location],
  );

  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('erp_menu_expanded') || '{}');
      return {
        overview: true,
        storefront: true,
        sales: true,
        purchasing: false,
        assets: false,
        manufacturing: false,
        finance: false,
        people: false,
        workspace: false,
        administration: false,
        ...saved,
      };
    } catch {
      return { overview: true, storefront: true, sales: true };
    }
  });

  useEffect(() => {
    localStorage.setItem('erp_menu_expanded', JSON.stringify(expandedSections));
  }, [expandedSections]);

  useEffect(() => {
    if (activeSectionId) {
      setExpandedSections((current) => (
        current[activeSectionId] ? current : { ...current, [activeSectionId]: true }
      ));
    }
  }, [activeSectionId]);

  useEffect(() => {
    document.title = `${user?.company_name || 'URBNSW'} ERP`;
  }, [user?.company_name]);

  const currentItem = visibleSections
    .flatMap((section) => section.items)
    .find((item) => isItemActive(item, location))
    || visibleSections.flatMap((section) => section.items).find((item) => targetParts(item.path).pathname === location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStorefrontPreview = async () => {
    setOpeningPreview(true);
    try {
      const response = await publicAPI.createAdminPreview();
      const { storefront_url: storefrontUrl, preview_token: previewToken } = response.data;
      const previewUrl = `${storefrontUrl}/?erp_preview=${encodeURIComponent(previewToken)}`;
      const popup = window.open(previewUrl, '_blank', 'noopener,noreferrer');
      if (!popup) window.location.assign(previewUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open the admin storefront preview');
    } finally {
      setOpeningPreview(false);
    }
  };

  const getInitials = (name = '') => name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div className="erp-container">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/55 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`erp-sidebar ${sidebarOpen ? 'open' : ''} lg:translate-x-0`} data-testid="sidebar">
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center border-b border-slate-200 px-5">
            <Link to="/dashboard" className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-black text-[#39FF14]">
                <span className="text-sm font-black tracking-[-0.08em]">U</span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-black tracking-[-0.02em] text-slate-950" title={user?.company_name || 'URBNSW'}>
                  {user?.company_name || 'URBNSW'}
                </p>
                <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400">ERP 2.0</p>
              </div>
            </Link>
            <button className="ml-auto rounded-md p-1.5 hover:bg-slate-100 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-3">
            {visibleSections.map((section) => {
              const SectionIcon = section.icon;
              const expanded = Boolean(expandedSections[section.id]);
              return (
                <div key={section.id} className="mb-1.5">
                  <button
                    onClick={() => setExpandedSections((current) => ({ ...current, [section.id]: !current[section.id] }))}
                    className={`grid w-full grid-cols-[18px_minmax(0,1fr)_16px] items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left text-[9.5px] font-bold uppercase tracking-[0.08em] transition-colors ${
                      activeSectionId === section.id ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                    }`}
                    aria-expanded={expanded}
                  >
                    <SectionIcon className="h-4 w-4 shrink-0 justify-self-start" />
                    <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left leading-4">{section.label}</span>
                    <ChevronDown className={`h-4 w-4 justify-self-end transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>

                  {expanded && (
                    <ul className="mt-1 space-y-0.5 pl-3">
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        const active = isItemActive(item, location);
                        return (
                          <li key={item.path}>
                            <Link
                              to={item.path}
                              className={`sidebar-item ${active ? 'sidebar-item-active' : ''} ${item.highlight && !active ? 'text-emerald-700' : ''}`}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${targetParts(item.path).pathname.slice(1)}`}
                            >
                              <ItemIcon className="h-4 w-4 shrink-0" />
                              <span className="truncate text-sm">{item.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="border-t border-slate-200 p-4">
            <div className="flex items-center gap-3 px-1">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-slate-950 text-sm font-bold text-[#39FF14]">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{user?.full_name}</p>
                <p className="truncate text-xs capitalize text-slate-500">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="erp-main">
        <header className="erp-header flex items-center justify-between px-4 sm:px-6" data-testid="header">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <button
              className="rounded-lg p-2 hover:bg-slate-100 lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold text-slate-900 sm:text-lg">{currentItem?.label || 'Dashboard'}</h1>
              <p className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 sm:block">
                {visibleSections.find((section) => section.id === activeSectionId)?.label || 'URBNSW ERP'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3">
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStorefrontPreview}
                disabled={openingPreview}
                className="gap-2 border-slate-300 bg-white text-slate-800 hover:border-slate-900 hover:bg-slate-950 hover:text-[#39FF14]"
                title="Open the live storefront with temporary admin access"
              >
                {openingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                <span className="hidden md:inline">Preview Storefront</span>
              </Button>
            )}

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-950 text-sm font-bold text-[#39FF14]">
                      {getInitials(user?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="hidden h-4 w-4 text-slate-500 sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings?tab=company')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Company Settings
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={handleStorefrontPreview}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Preview Storefront
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="erp-content animate-fade-in">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
